import React, { useCallback, useEffect, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import queryString from "query-string";
import { InlineWidget, CalendlyEventListener } from "react-calendly";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import TrustpilotInline from "../components/TrustpilotInline";
import getPostcodeArea from "../utils/postcodeArea";

import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";
const isBrowser = typeof window !== "undefined";

const advisers = [
	{
		file: "matthew.png",
		name: "matthew",
	},
	{
		file: "daniel-new.png",
		name: "daniel",
	},
	{
		file: "andrew.png",
		name: "andrew",
	},
];

function ConsultationForm({ gtmContainerId }) {
	const [formState, setFormState] = useState({
		firstName: "",
		lastName: "",
		email: "",
		number: "",
		type: "client",
	});
	const [isSubmitComplete, setIsSubmitComplete] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [matches, setMatches] = useState(false);

	useEffect(() => {
		if (!isBrowser || !gtmContainerId) return;

		window.dataLayer = window.dataLayer || [];
		function gtag() {
			window.dataLayer.push(arguments);
		}
		gtag("js", new Date());
		gtag("config", gtmContainerId);
	}, [gtmContainerId]);

	useEffect(() => {
		if (!isBrowser) return;

		const mediaMatch = window.matchMedia("(min-width: 800px)");
		const handleChange = (event) => setMatches(event.matches);

		setMatches(mediaMatch.matches);
		mediaMatch.addEventListener("change", handleChange);
		return () => mediaMatch.removeEventListener("change", handleChange);
	}, []);

	const trackCalendlyEvent = useCallback((interactionType) => {
		if (!isBrowser) return;
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: "calendly_event",
			interaction_type: interactionType,
		});
	}, []);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (isSubmitting) return;

		setIsSubmitting(true);
		if (isBrowser) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "edyn_form_submission",
				form_name: "book_consultation",
				lead_type: formState.type,
			});
		}

		let meta = {};
		if (isBrowser) {
			meta = queryString.parse(window.location.search);
		}

		try {
			await fetch("https://hooks.zapier.com/hooks/catch/6117983/bovj28r/", {
				method: "POST",
				mode: "cors",
				cache: "no-cache",
				credentials: "same-origin",
				headers: {},
				redirect: "follow",
				referrerPolicy: "no-referrer",
				body: JSON.stringify({
					firstName: formState.firstName,
					lastName: formState.lastName,
					email: formState.email,
					phone: formState.number,
					enquiryType: formState.type,
					formName: "book consultation",
					meta,
				}),
			});
		} catch (error) {
			// Booking remains available if the lead-capture request is interrupted.
			console.error("Could not submit consultation details", error);
		} finally {
			setIsSubmitting(false);
			setIsSubmitComplete(true);
		}
	};

	if (isSubmitComplete) {
		return (
			<div className="care-consultation-form care-consultation-form--calendar">
				<div className="care-consultation-form__heading">
					<p className="eyebrow">Choose a time</p>
					<h2>Find a time that feels right.</h2>
					<p>
						Your details have been saved. Choose a convenient time and a Care
						Advisor will call you.
					</p>
				</div>
				<CalendlyEventListener
					onEventTypeViewed={() => trackCalendlyEvent("event_type_viewed")}
					onEventScheduled={() => trackCalendlyEvent("event_scheduled")}
					onDateAndTimeSelected={() =>
						trackCalendlyEvent("date_and_time_selected")
					}
					onProfilePageViewed={() => trackCalendlyEvent("profile_page_viewed")}
				>
					<InlineWidget
						prefill={{
							name: `${formState.firstName} ${formState.lastName}`,
							email: formState.email,
							phone: formState.number,
						}}
						url="https://calendly.com/d/cqdy-tpd-9kv/looking-for-care-consultation"
						styles={{ height: matches ? "760px" : "990px" }}
					/>
				</CalendlyEventListener>
			</div>
		);
	}

	return (
		<div className="care-consultation-form">
			<div className="care-consultation-form__heading">
				<p className="eyebrow">Book your conversation</p>
				<h2>Choose a time to talk.</h2>
				<p>
					Share a few details and we will show the available appointments with a
					Care Advisor.
				</p>
			</div>

			<form onSubmit={handleSubmit}>
				<div className="care-consultation-form__grid">
					<div className="care-consultation-form__field">
						<label htmlFor="consultation-first-name">First name</label>
						<input
							autoComplete="given-name"
							id="consultation-first-name"
							name="firstName"
							required
							type="text"
							value={formState.firstName}
							onChange={(event) =>
								setFormState({ ...formState, firstName: event.target.value })
							}
						/>
					</div>
					<div className="care-consultation-form__field">
						<label htmlFor="consultation-last-name">Last name</label>
						<input
							autoComplete="family-name"
							id="consultation-last-name"
							name="lastName"
							required
							type="text"
							value={formState.lastName}
							onChange={(event) =>
								setFormState({ ...formState, lastName: event.target.value })
							}
						/>
					</div>
					<div className="care-consultation-form__field">
						<label htmlFor="consultation-email">Email address</label>
						<input
							autoComplete="email"
							id="consultation-email"
							name="email"
							required
							type="email"
							value={formState.email}
							onChange={(event) =>
								setFormState({ ...formState, email: event.target.value })
							}
						/>
					</div>
					<div className="care-consultation-form__field">
						<label htmlFor="consultation-phone">Phone number</label>
						<input
							autoComplete="tel"
							id="consultation-phone"
							name="phone"
							required
							type="tel"
							value={formState.number}
							onChange={(event) =>
								setFormState({ ...formState, number: event.target.value })
							}
						/>
					</div>
				</div>

				<div className="care-consultation-form__action">
					<button
						className="button button--primary"
						disabled={isSubmitting}
						type="submit"
					>
						{isSubmitting ? "Saving your details..." : "See available times"}
					</button>
					<p>
						By providing your details, you agree that Edyn can contact you by
						email or phone about our care services. You can withdraw your
						permission at any time. See our{" "}
						<a href="/privacy/">Privacy Policy</a>.
					</p>
				</div>
			</form>
		</div>
	);
}

export default function CareConsultation() {
	const [districtName, setDistrictName] = useState("");
	const {
		site: {
			siteMetadata: {
				appConfig: { gtmContainerId },
			},
		},
	} = useStaticQuery(graphql`
		query CareConsultationQuery {
			site {
				siteMetadata {
					appConfig {
						gtmContainerId
					}
				}
			}
		}
	`);

	useEffect(() => {
		if (!isBrowser) return;

		const { postcode } = queryString.parse(window.location.search);
		if (typeof postcode !== "string") return;

		const postcodeArea = getPostcodeArea(postcode);
		setDistrictName(postcodeArea?.areaName || "");
	}, []);

	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Book a Care Consultation | Edyn Care"
				description="Choose a convenient time to talk through live-in care with an Edyn Care Advisor. Get clear, practical guidance about the options and next steps."
				path="/care-consultation/"
			/>
			<Header />

			<main id="main-content" className="edyn-consultation">
				<section className="care-consultation-hero">
					<div className="site-shell care-consultation-hero__panel">
						<div className="care-consultation-hero__copy">
							<p className="eyebrow eyebrow--light">A care consultation</p>
							<h1>A calmer way to understand what comes next.</h1>
							<p className="care-consultation-hero__lead">
								Talk through what is changing at home with someone who listens.
								We will explain the options, answer your questions and help you
								find a practical next step, without pressure.
							</p>
							{districtName && (
								<p className="care-consultation-hero__availability">
									<span aria-hidden="true">&#10003;</span>
									We have carers in {districtName}
								</p>
							)}
						</div>

						<aside className="care-consultation-hero__aside">
							<div
								className="care-consultation-hero__advisers"
								role="img"
								aria-label="Edyn Family Care Advisors"
							>
								{advisers.map((adviser) => (
									<span
										className={`care-consultation-hero__adviser care-consultation-hero__adviser--${adviser.name}`}
										key={adviser.file}
									>
										<img
											alt=""
											loading="eager"
											src={`${imagePath}/team/${adviser.file}`}
										/>
									</span>
								))}
							</div>
							<p className="care-consultation-hero__aside-title">
								A real conversation with a Care Advisor.
							</p>
							<ul>
								<li>Bring the questions that are on your mind</li>
								<li>Understand what live-in care could look like</li>
								<li>Talk through likely costs and routes to pay for care</li>
								<li>Leave with clear, useful next steps</li>
							</ul>
							<a href="tel:02039709900">
								<span>Prefer to call?</span>
								020 3970 9900
							</a>
							<TrustpilotInline />
						</aside>
					</div>
				</section>

				<section className="care-consultation-booking" id="book-consultation">
					<div className="site-shell care-consultation-booking__grid">
						<div className="care-consultation-booking__intro">
							<p className="eyebrow">Your first conversation</p>
							<h2>Start with the situation, not a sales pitch.</h2>
							<p>
								You do not need a diagnosis, a completed care plan or all the
								answers. Tell us what is happening now and we will help you make
								sense of the choices.
							</p>
							<div className="care-consultation-booking__call">
								<span aria-hidden="true">&#8594;</span>
								<p>
									<strong>Rather talk now?</strong>
									<a href="tel:02039709900">Call 020 3970 9900</a>
								</p>
							</div>
						</div>
						<ConsultationForm gtmContainerId={gtmContainerId} />
					</div>
				</section>

				<section className="care-consultation-steps">
					<div className="site-shell">
						<div className="care-consultation-steps__heading">
							<p className="eyebrow">If live-in care feels right</p>
							<h2>What happens after the call.</h2>
						</div>
						<ol>
							<li>
								<span>01</span>
								<h3>A personal assessment</h3>
								<p>
									A Regional Care Manager gets to know the person, their
									routines, needs and the home.
								</p>
							</li>
							<li>
								<span>02</span>
								<h3>Your plan, costs and carers</h3>
								<p>
									We share a personal plan, a clear weekly price and funding
									routes, then introduce carers who fit the arrangement.
								</p>
							</li>
							<li>
								<span>03</span>
								<h3>Care that stays managed</h3>
								<p>
									Your Regional Care Manager and Care Coordinator stay close,
									coordinating the carer team, reviewing the plan and responding
									as needs change.
								</p>
							</li>
						</ol>
					</div>
				</section>
			</main>
			<Footer includeCTA={false} />
		</div>
	);
}
